import _ from 'lodash'
import { mapTypes, } from './SearchMapActionTypes'
import { getWashingtonBounds, } from '../util/mapCalcs'
import { filterAndRecalculateBounds, } from '../util/programMappers'
import { PROGRAM_SEARCH_CAT_ID, } from '../constants/other'

const DEFAULT_ZOOM = 7
const DEFAULT_CENTER = { lat: 47.24532933434984, lng: -120.83695125130188, }

const initialState = {
  activeCategoryFilter  : null,
  activeInfoWindowIndex : null,
  activeLocations       : [],  
  activeProgramId       : null,
  activeProgramTypeName : null, 
  bounds                : getWashingtonBounds(),
  center                : DEFAULT_CENTER,
  lastActiveProgramId   : null,
  lastLocationFilter    : null,
  locationFilter        : null,
  locations             : [],
  returnBounds          : null,
  returnCenter          : null,
  returnZoom            : null,
  showMobileMap         : false,
  viewBounds            : null,
  viewCenter            : null,
  viewZoom              : null,
  zoom                  : DEFAULT_ZOOM,
}

/**
 * Map Reducers
 */
function toggleMobileMap (state) {
  let nextState = { ...state, }
  if (state.showMobileMap && state.activeProgramId && state.activeProgramTypeName) {
    nextState = toggleActiveProgram(state, { 
      id   : state.activeProgramId, 
      type : state.activeProgramTypeName, 
    })
  }
  nextState.showMobileMap = !state.showMobileMap
  return nextState
}

function setZoom (state, { zoom, }) {
  if (state.zoom === zoom) {
    return state
  }
  return { ...state, zoom: zoom, }
}

function toggleActiveProgram (state, { id, type, }) {
  let nextState = state
  const { activeProgramId: oldId, activeProgramTypeName: oldType, } = state
  const nextId = id, nextType = type
  
  if (!oldId && !nextId && !oldType && !nextType) {
    return state
  }

  if ((oldId === nextId && oldType === nextType) || (oldId && oldType && (!nextId || !nextType))) {
    // the active program is sent again (toggle)
    // or there was an active program and now there is not
    nextState = {
      ...state,
      activeInfoWindowIndex     : null,
      activeLocations           : state.locations,
      activeProgramId           : null,
      activeProgramTypeName     : null,
      bounds                    : new google.maps.LatLngBounds(state.returnBounds),
      center                    : state.returnCenter,
      lastActiveProgramId       : oldId,
      lastActiveProgramTypeName : oldType,
      returnBounds              : null,
      returnCenter              : null,
      returnZoom                : null,
      showMobileMap             : false,
      zoom                      : state.returnZoom,
    }
  } else if (state.locations && state.locations.length > 0) {
    nextState = {
      ...state,
      activeInfoWindowIndex     : null,
      activeProgramId           : nextId,
      activeProgramTypeName     : nextType,
      lastActiveProgramId       : oldId,
      lastActiveProgramTypeName : oldType,
      showMobileMap             : true,
    }

    // if there was no active program and now there is
    if (!oldId && !oldType) {
      // save the bounds
      nextState.returnBounds = state.viewBounds
      nextState.returnCenter = state.viewCenter
      nextState.returnZoom = state.viewZoom
    }

    const newData = filterAndRecalculateBounds(state.locations, nextId, nextType)
    if (newData.locations.find(l => !state.viewBounds.contains({ lat: l.lat, lng: l.lon, }))){
      nextState.bounds = newData.bounds
      nextState.center = newData.center
      nextState.zoom = newData.zoom
    } else {
      nextState.bounds = state.viewBounds
      nextState.center = state.viewCenter
      nextState.zoom = state.viewZoom
    }
    // }
    nextState.activeLocations = newData.locations
  }
  return nextState
}

function locationsChanged (state, { locations, activeCat = null, returnMapState = null, }) {
  if (!locations) { console.warn('No bounds were provided') }

  let catFilter = state.activeCategoryFilter
  if (activeCat) {
    if (activeCat === 'all') {
      catFilter = null
    } else if (PROGRAM_SEARCH_CAT_ID[activeCat]) {
      catFilter =  PROGRAM_SEARCH_CAT_ID[activeCat]
    }
  }
  const newData = filterAndRecalculateBounds(locations, null, null, catFilter) 

  let bounds = newData.bounds, center = newData.center, zoom = newData.zoom
  if (returnMapState) {
    bounds = returnMapState.viewBounds
    center = returnMapState.viewCenter
    zoom = returnMapState.viewZoom
  } 
  return {
    ...state,
    activeCategoryFilter  : catFilter, 
    activeInfoWindowIndex : null,
    activeLocations       : newData.locations || initialState.locations,
    activeProgramId       : null,
    activeProgramTypeName : null,
    bounds                : bounds,
    center                : center,
    lastActiveProgramId   : state.activeProgramId,
    locations             : locations || initialState.locations,
    returnBounds          : null,
    returnCenter          : null,
    returnZoom            : null,
    showMobileMap         : true,
    zoom                  : zoom,
  }
}

function setActiveInfoWindowIndex (state, { index, }) {
  return { ...state, activeInfoWindowIndex: index, }
}

function resetActiveInfoWindowIndex (state) {
  return { ...state, activeInfoWindowIndex: null, }
}

function locationFilterGeocoded (state, { location, }) {
  if (!location) {
    return state
  }
  const nextFilter = { ...location, }

  if (_.isEqual(state.locationFilter, nextFilter)) {
    return state
  }

  if (!nextFilter) {
    return {
      ...state,
      lastLocationFilter : state.locationFilter,
      locationFilter     : initialState.locationFilter,
      center             : initialState.center,
      zoom               : initialState.zoom,
    }
  }

  let nextZoom = 10
  if (nextFilter.isPrecise) {
    nextZoom = 11
  }

  return {
    ...state,
    lastLocationFilter : state.locationFilter,
    locationFilter     : nextFilter,
    center             : { lat: nextFilter.lat, lng: nextFilter.lng, },
    zoom               : nextZoom, 
  }
}

function locationFilterReset (state) {
  if (initialState.bounds)
  {
    return {
      ...state,
      lastLocationFilter : state.locationFilter,
      locationFilter     : null,
      bounds             : initialState.bounds,
      center             : initialState.bounds.getCenter(),
      zoom               : initialState.zoom,
    }
  }
  return {
    ...state,
    lastLocationFilter : state.locationFilter,
    locationFilter     : null,
    bounds             : initialState.bounds,
    center             : initialState.center,
    zoom               : initialState.zoom,
  }
}


function viewBoundsChanged (state, { bounds, }) {
  if (_.isEqual(state.viewBounds, bounds)) {
    return state
  }
  return { ...state, viewBounds: bounds, }
}

function viewZoomChanged (state, { zoom, }) {
  if (state.viewZoom === zoom) {
    return state 
  }
  return { ...state, viewZoom: zoom, }
}

function viewCenterChanged (state, { center, }) {
  if (_.isEqual(state.viewCenter, center)) {
    return state 
  }
  return { ...state, viewCenter: center, }
}

function activeCategoryFilterChanged (state, { category, }) {
  let newCat = null
  if (category && PROGRAM_SEARCH_CAT_ID[category]) {
    newCat = PROGRAM_SEARCH_CAT_ID[category]
  }
  if (newCat === state.activeCategoryFilter) {
    return state
  }

  const newData = filterAndRecalculateBounds(state.locations, null, null, newCat)
  return {
    ...state,
    activeCategoryFilter      : newCat,
    activeInfoWindowIndex     : null,
    activeLocations           : newData.locations,
    activeProgramId           : null,
    activeProgramTypeName     : null,
    bounds                    : newData.bounds,
    center                    : newData.center,
    lastActiveProgramId       : state.activeProgramId,
    lastActiveProgramTypeName : state.activeProgramTypeName,
    returnBounds              : null,
    returnCenter              : null,
    returnZoom                : null,
    showMobileMap             : false,
    zoom                      : newData.zoom,
  } 
}

export function initMapState (nextState) {
  if (nextState) {
    const ret =  { 
      activeCategoryFilter  : nextState.activeCategoryFilter ? nextState.activeCategoryFilter : initialState.activeCategoryFilter,
      activeInfoWindowIndex : nextState.activeInfoWindowIndex ? nextState.activeInfoWindowIndex : initialState.activeInfoWindowIndex,
      activeLocations       : nextState.activeLocations ? nextState.activeLocations : initialState.activeLocations,
      activeProgramId       : nextState.activeProgramId ? nextState.activeProgramId : initialState.activeProgramId,
      activeProgramTypeName : nextState.activeProgramTypeName ? nextState.activeProgramTypeName : initialState.activeProgramTypeName,
      bounds                : nextState.bounds ? nextState.bounds : initialState.bounds,
      center                : nextState.center ? nextState.center : initialState.center,
      lastActiveProgramId   : nextState.lastActiveProgramId ? nextState.lastActiveProgramId : initialState.lastActiveProgramId,
      lastLocationFilter    : nextState.lastLocationFilter ? nextState.lastLocationFilter : initialState.lastLocationFilter,
      locationFilter        : nextState.locationFilter ? nextState.locationFilter : initialState.locationFilter,
      locations             : nextState.locations ? nextState.locations : initialState.locations,
      returnBounds          : nextState.returnBounds ? nextState.returnBounds : initialState.returnBounds,
      returnCenter          : nextState.returnCenter ? nextState.returnCenter : initialState.returnCenter,
      returnZoom            : nextState.returnZoom ? nextState.returnZoom : initialState.returnZoom,
      showMobileMap         : nextState.showMobileMap ? nextState.showMobileMap : initialState.showMobileMap,
      viewBounds            : nextState.viewBounds ? nextState.viewBounds : initialState.viewBounds,
      viewCenter            : nextState.viewCenter ? nextState.viewCenter : initialState.viewCenter,
      viewZoom              : nextState.viewZoom ? nextState.viewZoom : initialState.viewZoom,
      zoom                  : nextState.zoom ? nextState.zoom : initialState.zoom,
    }
    return ret
  }
  return { ...initialState, }
}

/**
 * Map Action Router
 */
function reducerActionSwitch (state, type, payload) {
  switch (type) {
  case mapTypes.activeCategoryFilterChanged : return activeCategoryFilterChanged(state, payload)
  case mapTypes.locationFilterGeocoded : return locationFilterGeocoded(state, payload)
  case mapTypes.locationFilterReset : return locationFilterReset(state)
  case mapTypes.locationsChanged : return locationsChanged(state, payload)
  case mapTypes.resetActiveInfoWindowIndex : return resetActiveInfoWindowIndex(state, payload)
  case mapTypes.setActiveInfoWindowIndex : return setActiveInfoWindowIndex(state, payload)
  case mapTypes.setZoom: return setZoom(state, payload)
  case mapTypes.toggleActiveProgram : return toggleActiveProgram(state, payload)
  case mapTypes.toggleMobileMap : return toggleMobileMap(state)
  case mapTypes.viewBoundsChanged : return viewBoundsChanged(state, payload)
  case mapTypes.viewCenterChanged : return viewCenterChanged(state, payload)
  case mapTypes.viewZoomChanged : return viewZoomChanged(state, payload)
  default: break
  }
  return state
}

/**
 * Program Map Reducer
 * @param {object} state current state
 * @param {object} action action
 * @returns next state
 */
export function mapReducer (state, action) {
  if (!action || !action.type) {
    return state
  }
  const nextState = reducerActionSwitch(state, action.type, action.payload)
  if (!nextState) {
    console.warn('MapReducer: action with type %s and payload %s produced an invalid state', action.type, JSON.stringify(action.payload))
  }
  return nextState
}